import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HotkeyService } from '../../../service/hotkey.service';
import { HotkeyHelpService } from '../../../service/admin/hotkey-help.service';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

type HotkeyView = {
  key: string;
  description?: string;
  icon: string;
};

type HotkeyGroup = {
  title: string;
  icon: string;
  wide: boolean;
  items: HotkeyView[];
};

@Component({
  standalone: true,
  selector: 'app-hotkey-help',
  imports: [CommonModule, TooltipModule],
  templateUrl: './hotkey-help.component.html',
  styleUrl: './hotkey-help.component.scss',
  host: {
    '[class.hk-inline]': 'inline',
  },
})
export class HotkeyHelpComponent implements OnInit, OnDestroy {
  /** When true, render the trigger inline (e.g. action bar) instead of fixed bottom-right. */
  @Input() inline = false;

  @ViewChild('backdropRef') backdropRef?: ElementRef<HTMLElement>;

  visible = false;
  groups: HotkeyGroup[] = [];
  totalCount = 0;

  private visibleSub?: Subscription;

  constructor(
    private hotkeyService: HotkeyService,
    public hotkeyHelpService: HotkeyHelpService
  ) {}

  ngOnInit() {
    this.visibleSub = this.hotkeyHelpService.visible$.subscribe((open) => {
      this.visible = open;
      if (open) {
        this.refreshHotkeys();
        setTimeout(() => this.attachBackdropToBody());
      }
    });
  }

  ngOnDestroy() {
    this.visibleSub?.unsubscribe();
    this.detachBackdropFromBody();
  }

  private attachBackdropToBody(): void {
    const backdrop = this.backdropRef?.nativeElement;
    if (!backdrop || backdrop.parentElement === document.body) {
      return;
    }
    document.body.appendChild(backdrop);
  }

  private detachBackdropFromBody(): void {
    const backdrop = this.backdropRef?.nativeElement;
    if (!backdrop || backdrop.parentElement !== document.body) {
      return;
    }
    backdrop.remove();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent) {
    if (this.visible && event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  refreshHotkeys() {
    const hotkeys = this.hotkeyService.getHotkeys().map((hk) => ({
      ...hk,
      icon: this.resolveShortcutIcon(hk.key, hk.description),
    }));
    this.totalCount = hotkeys.length;
    this.groups = this.buildGroups(hotkeys);
  }

  resolveShortcutIcon(key: string, description = ''): string {
    const iconByKey: Record<string, string> = {
      'alt.h': 'pi-home',
      'control./': 'pi-list-check',
      'control.shift.f': 'pi-search',
      'control.q': 'pi-users',
      'control.m': 'pi-bolt',
      'escape': 'pi-times',
      'alt.a': 'pi-calendar',
      'alt.l': 'pi-list',
      'alt.k': 'pi-th-large',
      'alt.x': 'pi-file-edit',
      'alt.p': 'pi-user-plus',
      'alt.m': 'pi-box',
      'alt.shift.m': 'pi-box',
      'alt.b': 'pi-wallet',
      'alt.c': 'pi-file',
      'alt.f': 'pi-search',
      'shift.r': 'pi-chart-line',
    };

    if (iconByKey[key]) {
      return iconByKey[key];
    }

    const desc = description.toLowerCase();
    if (desc.includes('search')) return 'pi-search';
    if (desc.includes('queue')) return 'pi-users';
    if (desc.includes('prescription') || desc.includes('medicine')) return 'pi-bolt';
    if (desc.includes('appointment')) return 'pi-calendar';
    if (desc.includes('patient')) return 'pi-id-card';
    if (desc.includes('certificate')) return 'pi-file';
    if (desc.includes('bill')) return 'pi-wallet';
    if (desc.includes('close')) return 'pi-times';
    if (desc.includes('home')) return 'pi-home';
    if (desc.includes('analytics') || desc.includes('report')) return 'pi-chart-line';

    return 'pi-circle';
  }

  formatKeyParts(combo: string): string[] {
    return combo.split('.').map((part) => {
      switch (part) {
        case 'control':
          return 'Ctrl';
        case 'alt':
          return 'Alt';
        case 'shift':
          return 'Shift';
        case 'meta':
          return 'Win';
        case 'escape':
          return 'Esc';
        case '/':
          return '/';
        default:
          return part.length === 1 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1);
      }
    });
  }

  formatKeyLabel(combo: string): string {
    return this.formatKeyParts(combo).join(' + ');
  }

  close() {
    this.hotkeyHelpService.close();
  }

  onBackdropClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('hk-backdrop')) {
      this.close();
    }
  }

  private buildGroups(hotkeys: HotkeyView[]): HotkeyGroup[] {
    const buckets = new Map<string, HotkeyView[]>([
      ['General', []],
      ['Consultation', []],
      ['Navigation', []],
      ['Other', []],
    ]);

    for (const hk of hotkeys) {
      buckets.get(this.categorize(hk.key))!.push(hk);
    }

    const defs: Array<{ title: string; icon: string; key: string }> = [
      { title: 'General', icon: 'pi-list-check', key: 'General' },
      { title: 'Consultation', icon: 'pi-users', key: 'Consultation' },
      { title: 'Navigation', icon: 'pi-compass', key: 'Navigation' },
      { title: 'Other', icon: 'pi-ellipsis-h', key: 'Other' },
    ];

    return defs
      .map((def) => ({
        title: def.title,
        icon: def.icon,
        wide: (buckets.get(def.key) ?? []).length > 3,
        items: buckets.get(def.key) ?? [],
      }))
      .filter((group) => group.items.length > 0);
  }

  private categorize(key: string): string {
    if (key === 'control./' || key === 'escape') {
      return 'General';
    }
    if (key.startsWith('control.')) {
      return 'Consultation';
    }
    if (key.startsWith('alt.') || key.startsWith('shift.')) {
      return 'Navigation';
    }
    return 'Other';
  }
}
